import axios from 'axios';
import EncryptedStorage from './constants/encryptedStorage';
import { API_HOST } from './constants/ApiConstants';
import { MODULES, PAGE_SIZE } from './constants';

const ACTION_KEYS = ['view', 'upsert', 'delete'];

type AccessResponseData = {
  permissions?: any;
  masking?: Record<string, boolean>;
};

type RegistrySubModule = {
  route?: string;
  name?: string;
  actions?: string[];
  masks?: string[];
};

type RegistryModule = {
  key?: string;
  name?: string;
  path?: string;
  service?: string;
  actions?: string[];
  submodules?: RegistrySubModule[];
};

type PermissionRegistry = {
  version?: number;
  maskable_fields?: string[];
  modules?: RegistryModule[];
};

type ModuleMetadata = {
  module?: string;
  subModules?: Array<{
    route?: string;
    url?: string;
  }>;
};

type AccessModule = {
  module?: string;
  path?: string;
  hasAccess?: boolean;
  subModules?: Array<{
    route?: string;
    url?: string;
  }>;
};

export const AUTH_ACCESS_REFRESHED_EVENT = 'auth-access-refreshed';

const parseMaybeJson = value => {
  if (typeof value !== 'string') {
    return value;
  }

  try {
    return JSON.parse(value);
  } catch (error) {
    return value;
  }
};

const normalizeAccessData = (source = {} as any): AccessResponseData => {
  const accessControl = parseMaybeJson(source?.ACCESS_CONTROL);

  if (source?.PERMISSIONS || source?.MASKING) {
    return {
      permissions: source?.PERMISSIONS,
      masking: source?.MASKING,
    };
  }

  if (accessControl?.permissions || accessControl?.masking) {
    return {
      permissions: accessControl?.permissions,
      masking: accessControl?.masking,
    };
  }

  if (accessControl?.grants || accessControl?.masking) {
    return {
      permissions: accessControl?.grants,
      masking: accessControl?.masking,
    };
  }

  return {
    permissions: accessControl,
    masking: source?.MASKING,
  };
};

const getStoredAccessData = () => {
  try {
    const data = JSON.parse(EncryptedStorage.getItem('_DATA_') || '{}');
    return normalizeAccessData(data);
  } catch (error) {
    return {};
  }
};

const getLegacyActions = (legacyPermissions, moduleKey, route?) => {
  const legacyModule = legacyPermissions?.find(module => module?.module === moduleKey);
  if (!legacyModule) {
    return [];
  }

  const source = route
    ? legacyModule?.subModules?.find(subModule => subModule?.route === route)
    : legacyModule;

  if (!source?.hasAccess) {
    return [];
  }

  return ACTION_KEYS.filter(actionKey => source?.[actionKey]);
};

const getAccessActions = (permissions, moduleKey, route?) => {
  if (!permissions || !moduleKey) {
    return [];
  }

  const normalizedPermissions = parseMaybeJson(permissions);

  if (Array.isArray(normalizedPermissions)) {
    return getLegacyActions(normalizedPermissions, moduleKey, route);
  }

  if (normalizedPermissions === '*') {
    return '*';
  }

  const moduleGrant = normalizedPermissions?.[moduleKey];
  if (!moduleGrant) {
    return [];
  }

  if (moduleGrant === '*') {
    return '*';
  }

  if (route) {
    if (Array.isArray(moduleGrant)) {
      return [];
    }

    const routeGrant = moduleGrant?.[route];
    return routeGrant || [];
  }

  if (Array.isArray(moduleGrant)) {
    return moduleGrant;
  }

  return moduleGrant?._ || [];
};

export const can = (
  moduleKey: string,
  action: string,
  route?: string,
  accessData?: AccessResponseData,
) => {
  const resolvedAccessData = accessData || getStoredAccessData();
  const actions = getAccessActions(resolvedAccessData?.permissions, moduleKey, route);
  return actions === '*' || (Array.isArray(actions) && actions.includes(action));
};

export const getAuthAccessData = authData => normalizeAccessData(authData);

export const canFromAuth = (authData, moduleKey: string, action: string, route?: string) =>
  can(moduleKey, action, route, getAuthAccessData(authData));

export const getRouteAccess = (authData, moduleKey: string, route?: string) => ({
  view: canFromAuth(authData, moduleKey, 'view', route),
  upsert: canFromAuth(authData, moduleKey, 'upsert', route),
  delete: canFromAuth(authData, moduleKey, 'delete', route),
  hasAccess: (
    canFromAuth(authData, moduleKey, 'view', route) ||
    canFromAuth(authData, moduleKey, 'upsert', route) ||
    canFromAuth(authData, moduleKey, 'delete', route)
  ),
});

export const getRegistryModule = (authData, moduleKey: string) =>
  authData?.PERMISSION_REGISTRY?.modules?.find(module => module?.key === moduleKey);

export const getRegistryRoute = (authData, moduleKey: string, route?: string) =>
  getRegistryModule(authData, moduleKey)?.submodules?.find(subModule => subModule?.route === route);

const buildActionFlags = (supportedActions: string[] = [], selectedActions: string[] | '*') =>
  ACTION_KEYS.reduce((flags, actionKey) => ({
    ...flags,
    [actionKey]: supportedActions.includes(actionKey) && (
      selectedActions === '*' ||
      (Array.isArray(selectedActions) && selectedActions.includes(actionKey))
    ),
  }), {});

export const buildModulesFromAccess = (
  registry: PermissionRegistry | undefined,
  accessData: AccessResponseData | undefined,
  moduleMetadata: ModuleMetadata[] = MODULES,
) => {
  const permissions = accessData?.permissions;
  const modules = registry?.modules || [];

  if (!permissions || !modules?.length) {
    return [];
  }

  return modules
    .map(module => {
      const configuredModule = moduleMetadata?.find(item => item?.module === module?.key);
      const moduleActions = getAccessActions(permissions, module?.key);
      const moduleFlags = buildActionFlags(module?.actions || [], moduleActions);
      const subModules = (module?.submodules || [])
        .map(subModule => {
          const configuredSubModule = configuredModule?.subModules?.find(item =>
            item?.route === subModule?.route || item?.url === subModule?.route,
          );
          const subModuleActions = getAccessActions(permissions, module?.key, subModule?.route);
          const flags = buildActionFlags(subModule?.actions || [], subModuleActions);
          const hasAccess = ACTION_KEYS.some(actionKey => flags?.[actionKey]);

          return {
            name: subModule?.route === 'studentPromote'
              ? 'Reassign Class'
              : subModule?.name || subModule?.route || '',
            url: configuredSubModule?.url || subModule?.route || '',
            route: configuredSubModule?.route || subModule?.route || '',
            actions: subModule?.actions || [],
            masks: subModule?.masks || [],
            hasAccess,
            ...flags,
          };
        })
        .filter(subModule => subModule?.hasAccess);

      const hasModuleAction = ACTION_KEYS.some(actionKey => moduleFlags?.[actionKey]);
      const hasAccess = module?.submodules?.length
        ? subModules?.length > 0
        : hasModuleAction;

      return {
        module: module?.key || '',
        name: module?.name || module?.key || '',
        path: module?.path || '',
        service: module?.service || '',
        actions: module?.actions || [],
        subModules,
        hasAccess,
        ...moduleFlags,
      };
    })
    .filter(module => module?.hasAccess);
};

export const applyAccessDataToAuth = (authData, registry, accessData) => ({
  ...authData,
  ACCESS_CONTROL: accessData,
  PERMISSIONS: accessData?.permissions || {},
  MASKING: accessData?.masking || {},
  PERMISSION_REGISTRY: registry || null,
  Modules: buildModulesFromAccess(registry, accessData),
});

export const getModuleLandingPath = (module?: AccessModule) => {
  if (!module?.path) {
    return '';
  }

  const firstSubModule = module?.subModules?.[0];
  if (firstSubModule?.route && String(module.path).endsWith('/*')) {
    return String(module.path).replace(
      '*',
      `${firstSubModule.route}/${firstSubModule.url || firstSubModule.route}/list`,
    );
  }

  return String(module.path).endsWith('/*')
    ? String(module.path).replace(/\/\*$/, '')
    : module.path;
};

export const getFirstAccessiblePath = (modules: AccessModule[] = [], excludedModules: string[] = []) => {
  const defaultExcludedModules = [
    'Masking_NOTAROUTER',
    'NotFoundPage',
    'InternalServerErrorPage',
  ];
  const excluded = [...defaultExcludedModules, ...excludedModules];
  const dashboardModule = modules?.find(module =>
    module?.hasAccess &&
    module?.module === 'Dashboard' &&
    !excluded.includes(module?.module || '') &&
    !!getModuleLandingPath(module),
  );

  if (dashboardModule) {
    return getModuleLandingPath(dashboardModule);
  }

  const academicYearModule = modules?.find(module =>
    module?.hasAccess &&
    module?.module === 'BranchSetup' &&
    !excluded.includes(module?.module || '') &&
    module?.subModules?.some(subModule => subModule?.route === 'academicYear') &&
    !!getModuleLandingPath({
      ...module,
      subModules: module?.subModules?.filter(subModule => subModule?.route === 'academicYear'),
    }),
  );

  if (academicYearModule) {
    return getModuleLandingPath({
      ...academicYearModule,
      subModules: academicYearModule?.subModules?.filter(subModule => subModule?.route === 'academicYear'),
    });
  }

  const firstModule = modules?.find(module =>
    module?.hasAccess &&
    !excluded.includes(module?.module || '') &&
    !!getModuleLandingPath(module),
  );

  return getModuleLandingPath(firstModule) || '/user-roles';
};

export const getAccessHeadersForAuth = authData => {
  const selectedAcademicYear = authData?.SELECTED_ACADEMIC_YEAR || (() => {
    try {
      return JSON.parse(EncryptedStorage.getItem('_SELECTED_ACADEMIC_YEAR_') || '{}');
    } catch (error) {
      return {};
    }
  })();
  const storedToken = EncryptedStorage.getItem('_UFT_');
  const token = storedToken || (authData?.ACCESS_TOKEN ? `Bearer ${authData.ACCESS_TOKEN}` : '');

  return {
    ...(token && { Authorization: token }),
    ...(authData?.DATABASE && { database: authData.DATABASE }),
    ...(authData?.PAYMENT_SERVICE_NAME && { 'payment-service-name': authData.PAYMENT_SERVICE_NAME }),
    ...(authData?.INSTITUTE_ID && { 'institute-id': authData.INSTITUTE_ID }),
    ...(authData?.BRANCH_ID && { 'branch-id': authData.BRANCH_ID }),
    ...(authData?.USER_ID && { 'USER-ID': authData.USER_ID }),
    ...(authData?.USER_NAME && { 'USER-NAME': authData.USER_NAME }),
    ...(authData?.USER_ROLE_NAME && { 'USER-ROLE': authData.USER_ROLE_NAME }),
    ...(authData?.STAFF_ROLE?.STAFF_ROLE_NAME && { 'STAFF-ROLE-NAME': authData.STAFF_ROLE.STAFF_ROLE_NAME }),
    'academic-year-id':
      selectedAcademicYear?.ACADEMIC_YEAR_ID || authData?.CURRENT_ACADEMIC_YEAR_ID || 0,
    ...(authData?.BRANCH_DID_NUMBER && { 'branch-did-number': authData.BRANCH_DID_NUMBER }),
    SOURCE: 'STAFF',
    'Ip-Address': authData?.IP_ADDRESS || EncryptedStorage.getItem('IP_ADDRESS') || '',
    Accept: 'application/json',
    'Content-Type': 'application/json',
  };
};

export const fetchAccessMeForAuth = authData =>
  axios.get(`${API_HOST}auth/access/me`, {
    headers: getAccessHeadersForAuth(authData),
  });

export const refreshStoredGrantedRoleAccess = async (newAccessToken?: string) => {
  const storedAuth = parseMaybeJson(EncryptedStorage.getItem('_DATA_') || '{}') || {};
  const canResolveAccess = storedAuth?.USER_ROLE_NAME !== 'STAFF' || !!storedAuth?.STAFF_ROLE?.STAFF_ROLE_NAME;

  if (!storedAuth?.IS_LOGGED_IN || !canResolveAccess) {
    return null;
  }

  const authData = {
    ...storedAuth,
    ...(newAccessToken ? { ACCESS_TOKEN: newAccessToken } : {}),
  };

  const accessResponse = await fetchAccessMeForAuth(authData);
  const refreshedAuthData = applyAccessDataToAuth(
    authData,
    authData?.PERMISSION_REGISTRY,
    accessResponse?.data?.data,
  );

  EncryptedStorage.setItem('_DATA_', JSON.stringify(refreshedAuthData));

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(AUTH_ACCESS_REFRESHED_EVENT, {
      detail: refreshedAuthData,
    }));
  }

  return refreshedAuthData;
};

/**
 * Resolve module access from scratch and persist it. Unlike
 * `refreshStoredGrantedRoleAccess`, this fetches the registry rather than
 * reusing the stored one, so it works when nothing was ever stored.
 */
export const resolveAndStoreAccess = async (newAccessToken?: string) => {
  const storedAuth = parseMaybeJson(EncryptedStorage.getItem('_DATA_') || '{}') || {};
  const canResolveAccess = storedAuth?.USER_ROLE_NAME !== 'STAFF' || !!storedAuth?.STAFF_ROLE?.STAFF_ROLE_NAME;

  if (!storedAuth?.BRANCH_ID || !canResolveAccess) {
    return null;
  }

  const authData = {
    ...storedAuth,
    ...(newAccessToken ? { ACCESS_TOKEN: newAccessToken } : {}),
  };
  const headers = getAccessHeadersForAuth(authData);

  const [registryResponse, accessResponse] = await Promise.all([
    axios.get(`${API_HOST}auth/access/registry`, { headers }),
    axios.get(`${API_HOST}auth/access/me`, { headers }),
  ]);

  const resolvedAuthData = applyAccessDataToAuth(
    authData,
    registryResponse?.data?.data,
    accessResponse?.data?.data,
  );

  // Subscription-gated, so it can still fail — but losing it must not cost us
  // the module access above. The app refetches it on boot when missing.
  try {
    const branchRoleResponse = await axios.get(
      `${API_HOST}data/branchRole/list/${authData.BRANCH_ID}?${PAGE_SIZE}`,
      { headers },
    );
    resolvedAuthData.BRANCH_ROLE_LIST = branchRoleResponse?.data?.data?.BranchRoleList || [];
  } catch (branchRoleError) {
    console.error('Unable to resolve branch roles:', branchRoleError);
  }

  EncryptedStorage.setItem('_DATA_', JSON.stringify(resolvedAuthData));

  return resolvedAuthData;
};

export const selectStaffRoleAccessForAuth = (authData, staffRole) =>
  axios.post(
    `${API_HOST}auth/access/select-staff-role`,
    {
      STAFF_ID: staffRole?.STAFF_ID,
      BRANCH_ROLE_ID: staffRole?.BRANCH_ROLE_ID || staffRole?.STAFF_ROLE_ID,
    },
    {
      headers: getAccessHeadersForAuth(authData),
    },
  );
